import { signal } from '@preact/signals';
import { ServerFrameSchema, type ServerFrame, type ClientFrame, type Envelope } from '@foyer/shared';

/**
 * Reconnecting WebSocket client. Auth happens in-band (the `hello` frame
 * carries the access token) since a browser can't set headers on the
 * WebSocket handshake itself — see packages/shared/src/ws.ts.
 */

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export const connectionState = signal<ConnectionState>('connecting');

type Handlers = {
  onMessage?: (envelope: Envelope) => void;
  onAck?: (messageId: string) => void;
  onReceipt?: (messageId: string, userId: string, kind: 'delivered' | 'read', at: number) => void;
  onTyping?: (convId: string, userId: string, on: boolean) => void;
  onSynced?: (count: number) => void;
};

const PING_INTERVAL_MS = 20_000;
const MAX_BACKOFF_MS = 30_000;
const MAX_QUEUE = 50;
/** Ephemeral by nature — resending a stale one after reconnect would be
 * actively wrong (a heartbeat or a "still typing" from minutes ago). */
const NEVER_QUEUE: ClientFrame['t'][] = ['ping', 'typing'];

export class WsClient {
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private backoff = 1000;
  private closedByUser = false;
  private getAccessToken: () => string | null;
  private handlers: Handlers;
  /**
   * Frames that couldn't go out immediately, flushed once the server
   * confirms it's ready. Exists because `send()` silently dropping a frame
   * whose socket wasn't OPEN at that exact instant had no recovery path
   * short of a fresh disconnect→reconnect transition — a multi-photo send
   * (several sequential uploads, long enough for a real mobile connection to
   * blip mid-send) could get lost with the connection never visibly
   * dropping from the app's point of view. Found live, not by inspection.
   */
  private queue: ClientFrame[] = [];

  constructor(getAccessToken: () => string | null, handlers: Handlers) {
    this.getAccessToken = getAccessToken;
    this.handlers = handlers;
  }

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    const token = this.getAccessToken();
    if (!token) {
      connectionState.value = 'closed';
      return;
    }

    connectionState.value = connectionState.value === 'open' ? 'reconnecting' : 'connecting';
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.ws = new WebSocket(`${proto}://${location.host}/ws`);

    this.ws.addEventListener('open', () => {
      this.send({ t: 'hello', accessToken: token });
      this.backoff = 1000; // reset on a successful connect
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_INTERVAL_MS);
    });

    this.ws.addEventListener('message', (ev: MessageEvent<string>) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }
      const result = ServerFrameSchema.safeParse(parsed);
      if (!result.success) return;
      this.handleFrame(result.data);
    });

    this.ws.addEventListener('close', () => this.scheduleReconnect());
    this.ws.addEventListener('error', () => this.ws?.close());
  }

  private handleFrame(frame: ServerFrame): void {
    switch (frame.t) {
      case 'ready':
        connectionState.value = 'open';
        this.flushQueue();
        break;
      case 'message':
        this.handlers.onMessage?.(frame.envelope);
        break;
      case 'receipt':
        this.handlers.onReceipt?.(frame.messageId, frame.userId, frame.kind, frame.at);
        break;
      case 'typing':
        this.handlers.onTyping?.(frame.convId, frame.userId, frame.on);
        break;
      case 'synced':
        this.handlers.onSynced?.(frame.count);
        break;
      case 'ack':
        // This is what clears a sent message's `pending` flag. Without it,
        // every message ever sent stays "pending" forever and gets resent
        // on every reconnect — found via a real duplicate-messages report,
        // not by inspection; the code previously (wrongly) assumed some
        // caller elsewhere was awaiting this frame directly. Nothing was.
        this.handlers.onAck?.(frame.id);
        break;
      case 'error':
        if (frame.fatal) this.ws?.close();
        break;
      // 'pong' needs no handler: it only exists to keep the connection alive.
    }
  }

  private scheduleReconnect(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.closedByUser) {
      connectionState.value = 'closed';
      return;
    }
    connectionState.value = 'reconnecting';
    const jitter = Math.random() * 0.3 * this.backoff;
    setTimeout(() => this.open(), this.backoff + jitter);
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
  }

  send(frame: ClientFrame): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return;
    }
    if (NEVER_QUEUE.includes(frame.t)) return;
    this.queue.push(frame);
    if (this.queue.length > MAX_QUEUE) this.queue.shift(); // oldest first, bounded
  }

  private flushQueue(): void {
    const pending = this.queue;
    this.queue = [];
    for (const frame of pending) this.send(frame);
  }

  close(): void {
    this.closedByUser = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close();
  }
}
